import { TEntityMilestone, TFeatureWorkflowInstance } from "entity-app/models";
import { InfoCircleOutlined } from "@ant-design/icons";

import { DateUtils } from "utils/dateutils";
import type { Dayjs } from "dayjs";
import { Button, Tooltip } from "antd";
import { useMemo, useRef, useState } from "react";
import DatePickerWithIcon from "components/date-picker-with-icon/date-picker-with-icon";
import { getDaysText } from "utils/utils";
import SetActualDatePicker from "components/date-block/set-actual-date-button";
import TimeEarlyStartIcon from "components/svg-icons/time-early-start";
import { ErrorMessages, EWorkflowStatusDataBlock } from "constants/index";
import EarlyStartModelConfirm from "popups/time-early-start-model";
import { FeatureTypes } from "entity-app/constants";
import {
  TDateBlockMode,
  TDateBlockPermissions,
  TWidthCalculation
} from "../../models";

const canShowCurrentDelay = (inputDateBlock: TFeatureWorkflowInstance) => {
  return (
    inputDateBlock?.calculation_details?.projecting_entity
      ?.delay_in_forwarded_date !== true
  );
};

const getEarliestFutureCommittedDate = (
  milestones: Array<TEntityMilestone>,
  milestoneIndex: number
) =>
  milestones
    .slice(milestoneIndex + 1)
    .reduce<string | null | undefined>((acc, ms) => {
      if (!ms.committed_date) return acc;
      if (!acc) return ms.committed_date;
      return DateUtils.dateTimeObj(ms.committed_date).isBefore(
        DateUtils.dateTimeObj(acc)
      )
        ? ms.committed_date
        : acc;
    }, undefined);

const getLatestPastCommittedDate = (
  milestones: Array<TEntityMilestone>,
  milestoneIndex: number
) =>
  milestones
    .slice(0, milestoneIndex)
    .reduce<string | null | undefined>((acc, ms) => {
      if (!ms.committed_date) return acc;
      if (!acc) return ms.committed_date;
      return DateUtils.dateTimeObj(ms.committed_date).isAfter(
        DateUtils.dateTimeObj(acc)
      )
        ? ms.committed_date
        : acc;
    }, undefined);

const createDisabledDateFn =
  (
    prevMilestone: TEntityMilestone | undefined,
    prevDateField: string | null | undefined,
    options?: {
      ignoreToday?: boolean;
      maxDate?: string | null | undefined;
    }
  ) =>
  (current: Dayjs) => {
    const currDateFromPicker = DateUtils.dateTimeObj(current)
      .hour(13)
      .minute(23)
      .second(24);
    const todayDate = DateUtils.dateTimeObj().hour(13).minute(23).second(23);
    const beforeToday = currDateFromPicker.isBefore(todayDate);
    const dependentDate = prevMilestone?.actual_date || prevDateField;
    const beforePrev = dependentDate
      ? currDateFromPicker.isBefore(DateUtils.dateTimeObj(dependentDate))
      : false;
    const disableForToday = !options?.ignoreToday && beforeToday;
    const afterMax =
      options?.maxDate != null
        ? currDateFromPicker.isAfter(DateUtils.dateTimeObj(options.maxDate))
        : false;
    return disableForToday || beforePrev || afterMax;
  };

const isCommittedDateDisabled = (
  current: Dayjs,
  prevMilestone: TEntityMilestone | undefined,
  latestPastCommittedDate: string | null | undefined,
  earliestFutureCommittedDate: string | null | undefined
) => {
  const baseDisabled = prevMilestone
    ? createDisabledDateFn(prevMilestone, prevMilestone.committed_date, {
        ignoreToday: true
      })(current)
    : createDisabledDateFn(undefined, null, { ignoreToday: true })(current);

  if (baseDisabled) return true;

  const currDateOnly = DateUtils.dateTimeObj(current).startOf("day");

  if (
    latestPastCommittedDate &&
    currDateOnly.isBefore(
      DateUtils.dateTimeObj(latestPastCommittedDate).startOf("day")
    )
  ) {
    return true;
  }

  if (
    earliestFutureCommittedDate &&
    currDateOnly.isAfter(
      DateUtils.dateTimeObj(earliestFutureCommittedDate).startOf("day")
    )
  ) {
    return true;
  }

  return false;
};

const isActualDateDisabled = (
  current: Dayjs,
  prevMilestone: TEntityMilestone | undefined
) => {
  const after = DateUtils.dateTimeObj(current).isAfter(DateUtils.dateTimeObj());
  if (!prevMilestone) return after;

  const currDateFromPicker = DateUtils.dateTimeObj(current)
    .hour(13)
    .minute(0)
    .second(0);
  const before = currDateFromPicker.isBefore(
    DateUtils.dateTimeObj(prevMilestone?.actual_date)
  );
  return before || after;
};

const getMilestoneBlockClassNames = (
  currentWorkflowMilestone: TEntityMilestone | undefined,
  milestone: TEntityMilestone
) => {
  const blockClass =
    "shrink-0 border border-solid text-xs h-52 relative rounded";
  const headerClass = "card-header";

  if (currentWorkflowMilestone !== milestone) {
    return { blockClass, headerClass };
  }

  if (!milestone.planned_date) {
    return {
      blockClass: `${blockClass} border-ciq-primary-900 border-2`,
      headerClass: `${headerClass} !bg-ciq-primary-900 border-b-0 !text-white`
    };
  }

  const isPlannedDateIsDelay =
    DateUtils.dateTimeObj()
      .startOf("D")
      .diff(DateUtils.dateTimeObj(milestone.planned_date).startOf("D"), "d") >
    0;

  return isPlannedDateIsDelay
    ? {
        blockClass: `${blockClass} next-milestone-delay-border`,
        headerClass: `${headerClass} next-milestone-delay-text`
      }
    : {
        blockClass: `${blockClass} next-milestone-on-track-border`,
        headerClass: `${headerClass} next-milestone-on-track-text`
      };
};

const EARLY_START_FEATURE_TYPES: Array<number> = [
  FeatureTypes.MATERIALS,
  FeatureTypes.BID_PACKAGE
];

// Only the wording differs between the two flows, so it is keyed off the type.
const EARLY_START_COPY: {
  [key: number]: { icon: string; title: string; prompt: string };
} = {
  [FeatureTypes.MATERIALS]: {
    icon: ErrorMessages.EarlyStartMsg,
    title: "Release Material Early",
    prompt:
      "Are you sure you want to release this Material before the linked predecessors are completed?"
  },
  [FeatureTypes.BID_PACKAGE]: {
    icon: ErrorMessages.EarlyTenderMsg,
    title: "Initiate an 'Early Bid Package'",
    prompt:
      "Are you sure you want to initiate an Early Bid Package before the linked Design Packages are completed?"
  }
};

const isEarlyStartMilestone = (
  permissions: TDateBlockPermissions,
  featureWorkflowInstance: TFeatureWorkflowInstance,
  milestone: TEntityMilestone
) =>
  (permissions.isDependedEntityInProgress ||
    !permissions.arePredecessorsComplete) &&
  EARLY_START_FEATURE_TYPES.includes(
    featureWorkflowInstance.feature_type_id as number
  ) &&
  milestone.sequence_no === 1;

const shouldShowProjectedDateDelay = (
  canShowCurrentDelayPermission: boolean,
  featureWorkflowInstance: TFeatureWorkflowInstance,
  currentWorkflowMilestone: TEntityMilestone | undefined,
  milestone: TEntityMilestone
) =>
  canShowCurrentDelayPermission &&
  featureWorkflowInstance.current_delay != null &&
  featureWorkflowInstance.current_delay > 0 &&
  currentWorkflowMilestone !== milestone;

function CommittedDateBlock({
  milestone,
  permissions,
  applyWorkflowChange,
  disabledCommittedDate
}: Readonly<{
  milestone: TEntityMilestone;
  permissions: TDateBlockPermissions;
  applyWorkflowChange: (data: any) => Promise<void>;
  disabledCommittedDate: (current: Dayjs) => boolean;
}>) {
  const childRefCommittedDate = useRef<any>();
  const renderCommittedDateFooter = (
    <div className="flex justify-center py-2">
      <Button
        onClick={() => {
          childRefCommittedDate.current?.close();
          const set = {
            milestones: [{ committed_date: null, id: milestone.id }]
          };
          applyWorkflowChange(set);
        }}
        data-testid="clear-committed-date"
      >
        Clear Date
      </Button>
    </div>
  );

  return (
    <div className="flex-1 pt-0">
      <div className="flex items-center">
        <span className="font-semibold mr-1">Committed date:</span>
        {!milestone.actual_date && (
          // Committed date constraints:
          // - Base rules from createDisabledDateFn (today/prev milestone)
          // - Lower bound: latest committed_date among all past milestones
          // - Upper bound: earliest committed_date among all future milestones
          <DatePickerWithIcon
            ref={childRefCommittedDate}
            value={
              milestone.committed_date
                ? DateUtils.dateTimeObj(milestone.committed_date)
                : undefined
            }
            disabled={!permissions.canEditCommittedDate}
            disabledDate={disabledCommittedDate}
            onChange={(date) => {
              if (date) {
                const saveDate = DateUtils.formatDateWithLunchTime(date);
                const set = {
                  milestones: [{ committed_date: saveDate, id: milestone.id }]
                };
                applyWorkflowChange(set);
              }
            }}
            renderExtraFooter={() => {
              return milestone.committed_date
                ? renderCommittedDateFooter
                : undefined;
            }}
          />
        )}
      </div>
      <span>
        {milestone.committed_date ? (
          DateUtils.format(milestone.committed_date)
        ) : (
          <span className="italic text-ciq-gray-700">Set date</span>
        )}
      </span>
    </div>
  );
}

function ProjectedDateBlock({
  milestone,
  prevMilestone,
  permissions,
  applyWorkflowChange,
  featureWorkflowInstance,
  currentWorkflowMilestone,
  canShowCurrentDelayPermission
}: Readonly<{
  milestone: TEntityMilestone;
  prevMilestone: TEntityMilestone | undefined;
  permissions: TDateBlockPermissions;
  applyWorkflowChange: (data: any) => Promise<void>;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  currentWorkflowMilestone: TEntityMilestone | undefined;
  canShowCurrentDelayPermission: boolean;
}>) {
  const canEditProjectedDate =
    featureWorkflowInstance.feature_type_id !== FeatureTypes.DESIGN_PACKAGE &&
    (featureWorkflowInstance.workflow_status as EWorkflowStatusDataBlock) ===
      EWorkflowStatusDataBlock.IN_PROGRESS &&
    milestone.sequence_no !== 1;

  if (milestone.actual_date || !milestone.projected_date) {
    return null;
  }

  return (
    <div className="flex-1 pt-0">
      <div className="flex items-center">
        <span className="font-semibold mr-1">Projected date:</span>
        {canEditProjectedDate && (
          <DatePickerWithIcon
            value={
              milestone.projected_date
                ? DateUtils.dateTimeObj(milestone.projected_date)
                : undefined
            }
            disabled={!permissions.canEditProjectedDate}
            disabledDate={createDisabledDateFn(
              prevMilestone,
              prevMilestone?.projected_date
            )}
            onChange={(date) => {
              if (date) {
                const saveDate = DateUtils.formatDateWithLunchTime(date);
                const set = {
                  milestones: [{ projected_date: saveDate, id: milestone.id }]
                };
                applyWorkflowChange(set);
              }
            }}
          />
        )}
      </div>
      <span
        className={
          shouldShowProjectedDateDelay(
            canShowCurrentDelayPermission,
            featureWorkflowInstance,
            currentWorkflowMilestone,
            milestone
          )
            ? "bg-delay pl-1 pr-2 py-[1px] rounded italic"
            : ""
        }
      >
        {DateUtils.format(milestone.projected_date)}
      </span>
    </div>
  );
}

function MileStoneBlock(props: {
  milestone: TEntityMilestone;
  prevMilestone: TEntityMilestone | undefined;
  nextMilestone: TEntityMilestone | undefined;
  currentWorkflowMilestone: TEntityMilestone | undefined;
  widthCal: TWidthCalculation;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  applyWorkflowChange: (data: any) => Promise<void>;
  governingTaskDiv: JSX.Element | null;
  permissions: TDateBlockPermissions;
  dateBlockMode?: TDateBlockMode;
  milestones: Array<TEntityMilestone>;
  milestoneIndex: number;
}) {
  const {
    milestone,
    widthCal,
    prevMilestone,
    nextMilestone,
    currentWorkflowMilestone,
    featureWorkflowInstance,
    applyWorkflowChange,
    governingTaskDiv,
    permissions,
    dateBlockMode = "projected",
    milestones,
    milestoneIndex
  } = props;
  const isMilestoneCommitmentMode = dateBlockMode === "milestoneCommitment";
  const isCommittedMode = dateBlockMode === "committed";

  const earliestFutureCommittedDate = useMemo(
    () => getEarliestFutureCommittedDate(milestones, milestoneIndex),
    [milestones, milestoneIndex]
  );

  const latestPastCommittedDate = useMemo(
    () => getLatestPastCommittedDate(milestones, milestoneIndex),
    [milestones, milestoneIndex]
  );

  const [isOpenEarlyStartModel, setIsOpenEarlyStartModel] = useState(false);

  const canShowCurrentDelayPermission = useMemo(
    () => canShowCurrentDelay(featureWorkflowInstance),
    [featureWorkflowInstance]
  );

  const infoMilestoneAction = (
    <Tooltip
      title={
        milestone.milestone_action || (
          <span className="text-gray-500 text-center px-3">
            No Milestone Action added
          </span>
        )
      }
    >
      <InfoCircleOutlined className="pl-1 text-gray-50 hover:text-white w-4 h-4" />
    </Tooltip>
  );

  const disabledActualDate = (current: Dayjs) =>
    isActualDateDisabled(current, prevMilestone);

  const disabledCommittedDate = (current: Dayjs) =>
    isCommittedDateDisabled(
      current,
      prevMilestone,
      latestPastCommittedDate,
      earliestFutureCommittedDate
    );

  const childRefPlannedDate = useRef<any>();
  const renderExtraFooterDiv = (
    <div className="flex justify-center py-2">
      <Button
        // disabled={cannotChangePlannedDate}
        onClick={() => {
          childRefPlannedDate.current?.close();

          const set = {
            milestones: [{ actual_date: null, id: milestone.id }]
          };
          applyWorkflowChange(set);
        }}
        data-testid="index-button-clear-date"
      >
        Clear Date
      </Button>
    </div>
  );

  const onChangeActualDate = (date: Dayjs | null) => {
    if (date) {
      const saveDate = DateUtils.formatDateWithLunchTime(date);
      const set = {
        milestones: [{ actual_date: saveDate, id: milestone.id }]
      };
      applyWorkflowChange(set);
    }
  };

  const actualDatePicker = (
    <DatePickerWithIcon
      value={
        milestone.actual_date
          ? DateUtils.dateTimeObj(milestone.actual_date)
          : undefined
      }
      onChange={onChangeActualDate}
      ref={childRefPlannedDate}
      disabledDate={disabledActualDate}
      renderExtraFooter={() => {
        return milestone.actual_date ? renderExtraFooterDiv : undefined;
      }}
    />
  );

  const plannedDiv = (
    <div className="flex-1 flex-col text-block-card space-y-1">
      <div className="font-semibold">Planned date: </div>
      <span>
        {milestone.planned_date
          ? DateUtils.format(milestone.planned_date)
          : "-"}
      </span>
    </div>
  );

  const canEditActualDate =
    (currentWorkflowMilestone === milestone ||
      currentWorkflowMilestone === nextMilestone) &&
    permissions.canEditActualDate;

  const dash = (
    isCommittedMode ? milestone.committed_date : milestone.projected_date
  )
    ? ""
    : "-";

  const isEarlyStart = isEarlyStartMilestone(
    permissions,
    featureWorkflowInstance,
    milestone
  );

  const earlyStartCopy =
    EARLY_START_COPY[featureWorkflowInstance.feature_type_id as number] ??
    EARLY_START_COPY[FeatureTypes.MATERIALS];

  const materialReleasedEarlyMsg = useMemo(() => {
    return (
      <div className="p-2">
        <div
          className={featureWorkflowInstance.wf_override_reason ? "mb-1.5" : ""}
        >
          {milestone.name} Early.
        </div>
        {featureWorkflowInstance.wf_override_reason && (
          <div>
            <span className="font-semibold">Reason - </span>{" "}
            <span>{featureWorkflowInstance.wf_override_reason}</span>
          </div>
        )}
      </div>
    );
  }, [featureWorkflowInstance.wf_override_reason, milestone.name]);

  const earlyStartActualDiv = (
    <div className="flex-1 flex-col text-block-card space-y-1">
      <div className="flex space-x-1 items-center">
        <div className="font-semibold">Actual date:</div>
        <Tooltip
          title={
            // Once the entity is released early (actual date set), the icon is
            // informational — show "Released Early", not the call-to-action.
            milestone.actual_date
              ? materialReleasedEarlyMsg
              : earlyStartCopy.icon
          }
        >
          <div className="min-w-0 pl-1 items-center flex">
            <TimeEarlyStartIcon
              className={
                canEditActualDate
                  ? "hover:opacity-100 opacity-80 cursor-pointer"
                  : "opacity-50 cursor-not-allowed"
              }
              onClick={() => {
                if (canEditActualDate) {
                  setIsOpenEarlyStartModel(true);
                }
              }}
            />
          </div>
        </Tooltip>
      </div>

      <span>
        {milestone.actual_date ? DateUtils.format(milestone.actual_date) : dash}
      </span>
    </div>
  );

  const actualDiv = !milestone.actual_date ? (
    <div>
      {canEditActualDate && (
        <div className="absolute bottom-2 left-[13px]">
          <SetActualDatePicker
            onChange={onChangeActualDate}
            disabledDate={disabledActualDate}
          />
        </div>
      )}
    </div>
  ) : (
    <div className="flex-1 flex-col text-block-card space-y-1">
      <div className="flex space-x-1 items-center">
        <div className="font-semibold">Actual date:</div>
        <div>{canEditActualDate && actualDatePicker}</div>
      </div>

      <span>
        {milestone.actual_date ? DateUtils.format(milestone.actual_date) : dash}
      </span>
    </div>
  );

  const classNameBlock = useMemo(
    () => getMilestoneBlockClassNames(currentWorkflowMilestone, milestone),
    [currentWorkflowMilestone, milestone]
  );

  const currentDelayDiv = (
    <div className="h-12 w-full">
      {canShowCurrentDelayPermission &&
        featureWorkflowInstance.current_delay != null &&
        featureWorkflowInstance.current_delay > 0 &&
        currentWorkflowMilestone === milestone && (
          <div className="bg-delay text-xs p-1 flex items-center rounded-b">
            <div className="grow">
              <div className="flex items-center justify-center lowercase">
                <span className="font-bold pr-1">
                  {featureWorkflowInstance.current_delay}{" "}
                  {getDaysText(featureWorkflowInstance.current_delay)}
                </span>
                <span>late</span>
              </div>
            </div>
            <Tooltip
              title={`As of today, ${DateUtils.format(
                featureWorkflowInstance.today_date
              )}.`}
              styles={{ container: { color: "#000000CC" } }}
            >
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        )}
      {!nextMilestone && governingTaskDiv}
    </div>
  );

  return (
    <div
      style={{ width: widthCal.milestonesWidth }}
      data-testid={`timeline-milestones-milestoneblock-${milestone.id}`}
    >
      <div
        className={classNameBlock.blockClass}
        data-testid={`timeline-milestones-block-${milestone.id}`}
      >
        <div
          className={classNameBlock.headerClass}
          data-testid={`timeline-milestones-header-${milestone.id}`}
        >
          <Tooltip title={milestone.name}>
            <div
              className="ellipsis-clamp-2"
              data-testid={`timeline-milestones-title-${milestone.id}`}
            >
              {milestone.name}
            </div>
          </Tooltip>

          {currentWorkflowMilestone === milestone && infoMilestoneAction}
        </div>
        <div
          className="p-3"
          data-testid={`timeline-milestones-content-${milestone.id}`}
        >
          <div className="text-xs h-full space-y-3">
            {!isMilestoneCommitmentMode ? plannedDiv : null}
            {isMilestoneCommitmentMode ? (
              <CommittedDateBlock
                milestone={milestone}
                permissions={permissions}
                applyWorkflowChange={applyWorkflowChange}
                disabledCommittedDate={disabledCommittedDate}
              />
            ) : null}
            {isCommittedMode ? (
              <CommittedDateBlock
                milestone={milestone}
                permissions={permissions}
                applyWorkflowChange={applyWorkflowChange}
                disabledCommittedDate={disabledCommittedDate}
              />
            ) : (
              <ProjectedDateBlock
                milestone={milestone}
                prevMilestone={prevMilestone}
                permissions={permissions}
                applyWorkflowChange={applyWorkflowChange}
                featureWorkflowInstance={featureWorkflowInstance}
                currentWorkflowMilestone={currentWorkflowMilestone}
                canShowCurrentDelayPermission={canShowCurrentDelayPermission}
              />
            )}
            {isEarlyStart ? earlyStartActualDiv : actualDiv}
          </div>
        </div>
      </div>
      {currentDelayDiv}
      {isOpenEarlyStartModel && (
        <EarlyStartModelConfirm
          isModalOpen={isOpenEarlyStartModel}
          setIsModalOpen={setIsOpenEarlyStartModel}
          milestoneName={milestone.name}
          existingActualDate={milestone.actual_date}
          existingReason={featureWorkflowInstance.wf_override_reason || null}
          title={earlyStartCopy.title}
          confirmationPrompt={earlyStartCopy.prompt}
          onOk={(prop) => {
            const saveDate = prop.actualDate
              ? DateUtils.formatDateWithLunchTime(prop.actualDate)
              : null;

            const set = {
              wf_override_reason: prop.reason,
              milestones: [
                {
                  actual_date: saveDate,
                  id: milestone.id
                }
              ]
            };
            return applyWorkflowChange(set);
          }}
          disabledDate={disabledActualDate}
          data-testid={`timeline-milestones-earlystart-confirm-${milestone.id}`}
        />
      )}
    </div>
  );
}

export default function TimelineMilestones(props: {
  milestones: Array<TEntityMilestone>;
  currentMilestone: TEntityMilestone | undefined;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  widthCal: TWidthCalculation;
  applyWorkflowChange: (data: any) => Promise<void>;
  governingTaskDiv: JSX.Element | null;
  permissions: TDateBlockPermissions;
  dateBlockMode?: TDateBlockMode;
}) {
  const {
    milestones,
    currentMilestone,
    widthCal,
    featureWorkflowInstance,
    applyWorkflowChange,
    governingTaskDiv,
    permissions,
    dateBlockMode = "projected"
  } = props;
  const { length } = milestones;

  return (
    <div
      className="flex h-[200px] space-x-6"
      data-testid="timeline-milestones-root"
    >
      {milestones.map((m, index) => {
        const prev = index > 0 ? milestones[index - 1] : undefined;
        const next = index < length - 1 ? milestones[index + 1] : undefined;
        return (
          <MileStoneBlock
            milestone={m}
            widthCal={widthCal}
            prevMilestone={prev}
            nextMilestone={next}
            currentWorkflowMilestone={currentMilestone}
            featureWorkflowInstance={featureWorkflowInstance}
            key={`TimelineMilestone_${m.id}`}
            applyWorkflowChange={applyWorkflowChange}
            governingTaskDiv={governingTaskDiv}
            permissions={permissions}
            dateBlockMode={dateBlockMode}
            milestones={milestones}
            milestoneIndex={index}
            data-testid={`timeline-milestones-milestoneblock-wrapper-${m.id}`}
          />
        );
      })}
    </div>
  );
}
